import useHelpers from 'hooks/helpers';
import usePayload from 'hooks/useFundDetailsRequestPayload';
import { useI18n } from 'next-localization';
import { useSelector } from 'react-redux';
import { getSelectedShareClass } from '@/store/fundDetails';
import {
  useFetchKeyInformationTabDataQuery,
  useFetchDisclaimerDataQuery,
  useLazyFetchKeyInfoCefFieldsQuery,
} from '@/store/fundDetails/services/fundDetailsApi';
import dictionaryItems, { getDictionaryItemFormatter } from '@/constants/keyInfoTableFields';
import FundDetailsTabRow from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabRow';
import FundDetailsTabSkeleton from 'components/Pages/FundsCenter/FundDetails/FundDetailsTabSkeleton';
import AlertMessageBase from 'components/Generic/AlertMessageBase';
import { useEffect, useMemo, useState } from 'react';
import ManualDisclaimer from 'components/Generic/ManualDisclaimer';
import { getFundHeroType } from '@/store/fundDetails';
import { CefManualKeyInfoFragment } from 'components/graphql/CefManualKeyInfoSearch.graphql';
import { OrmKeyInfoValueDate } from 'model/ApiGateway';

type UnwrapValue<T> = T extends { value?: infer Q } ? Q : never;

type UnwrapRecord<R extends Record<string, any>> = {
  [key in keyof R]: UnwrapValue<R[key]>;
};

const FundDetailsKeyInformationTab = () => {
  const { getErrorDictionary, isExperienceEditor, formatCurrency, formatNumber, localizeDate } =
    useHelpers();
  const selectedShareClass = useSelector(getSelectedShareClass);
  const fundHeroType = useSelector(getFundHeroType);
  const payload = usePayload();
  const { t } = useI18n();
  const [cefFields, setCefFields] = useState<CefManualKeyInfoFragment>();

  const { data, error, isLoading } = useFetchKeyInformationTabDataQuery(payload, {
    skip: isExperienceEditor,
  });
  const disclaimerData = useFetchDisclaimerDataQuery(payload, {
    skip: isExperienceEditor,
  });
  const [fetchData, { isFetching: isCefFieldsLoading }] = useLazyFetchKeyInfoCefFieldsQuery();

  const mergedKeyInformationData = useMemo(() => {
    const mappedCEFFields: Partial<UnwrapRecord<CefManualKeyInfoFragment>> = {};
    if (cefFields) {
      for (const [key, value] of Object.entries(cefFields)) {
        mappedCEFFields[key] = value.value;
      }
    }

    const mergedData = {
      ...data?.content,
      manualFund: { ...mappedCEFFields },
    };
    return mergedData;
  }, [data, cefFields]);

  const footnote = useMemo(() => {
    return data?.content?.staticText?.fundStatisticsFootnote;
  }, [data]);

  const currencySign = useMemo(() => {
    const dictionaryKey = t('fundDetailsKeyInfoCurrencySymbol');
    return dictionaryKey && dictionaryKey !== 'fundDetailsKeyInfoCurrencySymbol'
      ? dictionaryKey
      : '';
  }, [t]);

  const formatItemValue = (
    item: string | OrmKeyInfoValueDate,
    key: keyof typeof dictionaryItems
  ) => {
    const formatter = getDictionaryItemFormatter({ key, item });
    const priceCurrencyCode = selectedShareClass?.prices?.priceCurrencyCode || 'GBP';
    return (
      formatter({
        item,
        currencySign,
        priceCurrencyCode,
        t,
        formatCurrency,
        formatNumber,
        localizeDate,
      }) ?? item
    );
  };
  const haveFootnote = (key: string) => {
    return key === 'wal' || key === 'wam' ? '[1]' : '';
  };

  const ComponentContent = () => {
    return (
      <>
        <table aria-label="Fund details codes tab table" className="w-100">
          <thead hidden>
            <tr>
              <th scope="col">Fund key information name</th>
              <th scope="col">Fund key information value</th>
            </tr>
          </thead>
          <tbody className="d-flex flex-column">
            {Object.entries(mergedKeyInformationData).map(([, data]) => (
              <>
                {Object.entries(data).map(
                  ([key, value]) =>
                    key !== 'fundStatisticsFootnote' &&
                    dictionaryItems[key] && (
                      <tr
                        key={key}
                        className="d-flex"
                        style={{
                          order: key === 'sdrLabel' || key === 'sdrClassification' ? -1 : undefined,
                          borderBottom: '1px solid rgba(0, 0, 0, 0.3)',
                        }}
                      >
                        <td className="h6 w-50 py-4">
                          {t(dictionaryItems[key].name) + haveFootnote(key)}
                        </td>
                        <td className="body-default w-50 py-4">{formatItemValue(value, key)}</td>
                      </tr>
                    )
                )}
              </>
            ))}
          </tbody>
        </table>
        {footnote && (
          <div className="container mt-4 py-2 px-2 background-digital-light-grey">
            <p className="footnote">{'[1] ' + footnote}</p>
          </div>
        )}
      </>
    );
  };

  useEffect(() => {
    const fetchCefFields = async () => {
      try {
        const { data } = await fetchData({
          fundId: payload.fund ?? '',
          language: payload.language,
        });
        setCefFields(data);
      } catch (e) {
        console.log(e);
      }
    };
    // check if current fund is CEF fund
    if (fundHeroType === 1 || fundHeroType === 2) {
      fetchCefFields();
    }
  }, [fundHeroType]);

  return (
    <FundDetailsTabRow fullWidth={true} className="mt-4">
      {isLoading || isCefFieldsLoading ? (
        <FundDetailsTabSkeleton />
      ) : error ? (
        <AlertMessageBase type="error" title={t(getErrorDictionary(error))} />
      ) : mergedKeyInformationData ? (
        <ComponentContent />
      ) : (
        <div className="body-default">{t('noResultsMessage')}</div>
      )}

      {disclaimerData.data && disclaimerData.data.length !== 0 && (
        <ManualDisclaimer disclaimerData={disclaimerData.data} className="mt-4" />
      )}
    </FundDetailsTabRow>
  );
};

export default FundDetailsKeyInformationTab;
